/**
 * CapExEditor - Admin Spreadsheet-like Editor for Capital Cost Rates
 * 
 * Allows admins to manage pricing categories and line items that apply to all users.
 * Structure mirrors the frontend CapEx breakdown with editable rates.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Snackbar,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    ExpandMore as ExpandMoreIcon,
    AttachMoney as MoneyIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { useAuth } from '@vortx/shared-ui';

// Unit options for rate items
const UNIT_OPTIONS = [
    { value: '$/kW', label: '$/kW (Power)' },
    { value: '$/kWh', label: '$/kWh (Energy)' },
    { value: '$/system', label: '$/system (Fixed)' },
    { value: '%', label: '% (Percentage)' },
    { value: '$/sqft', label: '$/sqft (Area)' },
    { value: '$/unit', label: '$/unit (Quantity)' }
];

function CapExEditor({ scope = 'global' }) {
    const { currentUser } = useAuth();
    const [structure, setStructure] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [hasChanges, setHasChanges] = useState(false);

    // Dialog states
    const [addCategoryDialog, setAddCategoryDialog] = useState(false);
    const [addItemDialog, setAddItemDialog] = useState({ open: false, categoryId: null });
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newItem, setNewItem] = useState({ name: '', rate: 0, unit: '$/kW', description: '' });

    // Determine API endpoint based on scope
    const apiEndpoint = scope === 'global'
        ? '/api/site_dna/v1/projects/admin/capex'
        : '/api/site_dna/v1/projects/admin/capex?scope=company';

    // Fetch CapEx structure from backend
    const fetchStructure = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        setError(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(apiEndpoint, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) {
                if (res.status === 403) {
                    throw new Error('Admin access required');
                }
                throw new Error('Failed to fetch CapEx structure');
            }
            const data = await res.json();
            setStructure(data);
            setHasChanges(false);
        } catch (err) {
            console.error('Error fetching CapEx:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser, apiEndpoint]);

    useEffect(() => {
        fetchStructure();
    }, [fetchStructure]);

    // Save entire structure
    const handleSave = async () => {
        if (!currentUser || !structure) return;
        setSaving(true);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(apiEndpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(structure)
            });
            if (!res.ok) throw new Error('Failed to save CapEx structure');
            setSuccessMessage(`${scope === 'global' ? 'Global' : 'Company'} rates saved successfully!`);
            setHasChanges(false);
        } catch (err) {
            console.error('Error saving CapEx:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Add new category
    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) return;

        const newCategory = {
            id: newCategoryName.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and'),
            name: newCategoryName,
            order: structure.categories.length,
            items: []
        };

        setStructure(prev => ({
            ...prev,
            categories: [...prev.categories, newCategory]
        }));
        setHasChanges(true);
        setNewCategoryName('');
        setAddCategoryDialog(false);
    };

    // Delete category
    const handleDeleteCategory = (categoryId) => {
        setStructure(prev => ({
            ...prev,
            categories: prev.categories.filter(c => c.id !== categoryId)
        }));
        setHasChanges(true);
    };

    // Add new item to category
    const handleAddItem = () => {
        if (!newItem.name.trim()) return;

        const categoryId = addItemDialog.categoryId;
        const itemData = {
            id: newItem.name.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and'),
            name: newItem.name,
            rate: parseFloat(newItem.rate) || 0,
            unit: newItem.unit,
            description: newItem.description || '',
            order: 0
        };

        setStructure(prev => ({
            ...prev,
            categories: prev.categories.map(c => {
                if (c.id === categoryId) {
                    itemData.order = c.items.length;
                    return { ...c, items: [...c.items, itemData] };
                }
                return c;
            })
        }));
        setHasChanges(true);
        setNewItem({ name: '', rate: 0, unit: '$/kW', description: '' });
        setAddItemDialog({ open: false, categoryId: null });
    };

    // Delete item from category
    const handleDeleteItem = (categoryId, itemId) => {
        setStructure(prev => ({
            ...prev,
            categories: prev.categories.map(c => {
                if (c.id === categoryId) {
                    return { ...c, items: c.items.filter(i => i.id !== itemId) };
                }
                return c;
            })
        }));
        setHasChanges(true);
    };

    // Update item rate
    const handleUpdateItemRate = (categoryId, itemId, newRate) => {
        setStructure(prev => ({
            ...prev,
            categories: prev.categories.map(c => {
                if (c.id === categoryId) {
                    return {
                        ...c,
                        items: c.items.map(i =>
                            i.id === itemId ? { ...i, rate: parseFloat(newRate) || 0 } : i
                        )
                    };
                }
                return c;
            })
        }));
        setHasChanges(true);
    };

    // Update item field
    const handleUpdateItemField = (categoryId, itemId, field, value) => {
        setStructure(prev => ({
            ...prev,
            categories: prev.categories.map(c => {
                if (c.id === categoryId) {
                    return {
                        ...c,
                        items: c.items.map(i =>
                            i.id === itemId ? { ...i, [field]: value } : i
                        )
                    };
                }
                return c;
            })
        }));
        setHasChanges(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: vortxColors.gold }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
                <Button onClick={fetchStructure} size="small" sx={{ ml: 2 }}>
                    Retry
                </Button>
            </Alert>
        );
    }

    return (
        <Card sx={{ bgcolor: vortxColors.charcoal, mb: 3 }}>
            <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <MoneyIcon sx={{ color: vortxColors.gold, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: vortxColors.white, fontWeight: 600 }}>
                            Capital Cost Rates {scope === 'company' ? '(Company)' : '(Global)'}
                        </Typography>
                        {hasChanges && (
                            <Chip
                                label="Unsaved Changes"
                                size="small"
                                sx={{ bgcolor: alpha(vortxColors.gold, 0.2), color: vortxColors.gold }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={fetchStructure}
                            variant="outlined"
                            size="small"
                            sx={{ borderColor: vortxColors.stone, color: vortxColors.stone }}
                        >
                            Refresh
                        </Button>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => setAddCategoryDialog(true)}
                            variant="outlined"
                            size="small"
                            sx={{ borderColor: vortxColors.gold, color: vortxColors.gold }}
                        >
                            Add Category
                        </Button>
                        <Button
                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            onClick={handleSave}
                            variant="contained"
                            size="small"
                            disabled={saving || !hasChanges}
                            sx={{
                                bgcolor: vortxColors.gold,
                                color: vortxColors.green,
                                '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) }
                            }}
                        >
                            {saving ? 'Saving...' : 'Save All'}
                        </Button>
                    </Box>
                </Box>

                <Typography variant="body2" sx={{ color: vortxColors.stone, mb: 3 }}>
                    {scope === 'global'
                        ? "Manage global default capital cost rates. These rates are used as defaults for all companies."
                        : "Manage your company's capital cost rates. These override global defaults for your company's users."}
                </Typography>

                {/* Categories as Accordions */}
                {structure?.categories?.map((category, catIndex) => (
                    <Accordion
                        key={category.id}
                        sx={{
                            bgcolor: alpha(vortxColors.green, 0.5),
                            '&:before': { display: 'none' },
                            mb: 1
                        }}
                        defaultExpanded={catIndex < 2}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: vortxColors.stone }} />}
                            sx={{
                                '&:hover': { bgcolor: alpha(vortxColors.green, 0.7) }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Typography sx={{ color: vortxColors.white, fontWeight: 600, flex: 1 }}>
                                    {category.name}
                                </Typography>
                                <Chip
                                    label={`${category.items?.length || 0} items`}
                                    size="small"
                                    sx={{ bgcolor: alpha(vortxColors.gold, 0.2), color: vortxColors.gold }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {/* Category Actions */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => setAddItemDialog({ open: true, categoryId: category.id })}
                                    size="small"
                                    sx={{ color: vortxColors.gold }}
                                >
                                    Add Item
                                </Button>
                                <Box sx={{ flex: 1 }} />
                                <Tooltip title="Delete Category">
                                    <IconButton
                                        onClick={() => handleDeleteCategory(category.id)}
                                        size="small"
                                        sx={{ color: '#e74c3c' }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Items Table */}
                            <TableContainer component={Paper} sx={{ bgcolor: vortxColors.green }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: vortxColors.stone, width: '30%' }}>Item Name</TableCell>
                                            <TableCell sx={{ color: vortxColors.stone, width: '15%' }}>Rate</TableCell>
                                            <TableCell sx={{ color: vortxColors.stone, width: '15%' }}>Unit</TableCell>
                                            <TableCell sx={{ color: vortxColors.stone, width: '30%' }}>Description</TableCell>
                                            <TableCell sx={{ color: vortxColors.stone, width: '10%' }} align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {category.items?.map((item) => (
                                            <TableRow key={item.id} hover>
                                                <TableCell>
                                                    <TextField
                                                        value={item.name}
                                                        onChange={(e) => handleUpdateItemField(category.id, item.id, 'name', e.target.value)}
                                                        variant="standard"
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            input: { color: vortxColors.white },
                                                            '& .MuiInput-underline:before': { borderColor: 'transparent' }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={item.rate}
                                                        onChange={(e) => handleUpdateItemRate(category.id, item.id, e.target.value)}
                                                        type="number"
                                                        variant="standard"
                                                        size="small"
                                                        sx={{
                                                            input: { color: vortxColors.gold, fontWeight: 600 },
                                                            '& .MuiInput-underline:before': { borderColor: 'transparent' },
                                                            width: 100
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={item.unit}
                                                        onChange={(e) => handleUpdateItemField(category.id, item.id, 'unit', e.target.value)}
                                                        variant="standard"
                                                        size="small"
                                                        sx={{
                                                            color: vortxColors.stone,
                                                            '& .MuiSelect-icon': { color: vortxColors.stone }
                                                        }}
                                                    >
                                                        {UNIT_OPTIONS.map(opt => (
                                                            <MenuItem key={opt.value} value={opt.value}>{opt.value}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={item.description || ''}
                                                        onChange={(e) => handleUpdateItemField(category.id, item.id, 'description', e.target.value)}
                                                        variant="standard"
                                                        size="small"
                                                        fullWidth
                                                        placeholder="Description..."
                                                        sx={{
                                                            input: { color: vortxColors.stone, fontSize: '0.85rem' },
                                                            '& .MuiInput-underline:before': { borderColor: 'transparent' }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Delete Item">
                                                        <IconButton
                                                            onClick={() => handleDeleteItem(category.id, item.id)}
                                                            size="small"
                                                            sx={{ color: '#e74c3c' }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {category.items?.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} sx={{ textAlign: 'center', color: vortxColors.stone, py: 3 }}>
                                                    No items in this category. Click "Add Item" to add one.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                ))}

                {/* Metadata */}
                {structure?.metadata?.last_updated_at && (
                    <Typography variant="caption" sx={{ color: vortxColors.stone, mt: 2, display: 'block' }}>
                        Last updated: {new Date(structure.metadata.last_updated_at).toLocaleString()}
                    </Typography>
                )}
            </CardContent>

            {/* Add Category Dialog */}
            <Dialog open={addCategoryDialog} onClose={() => setAddCategoryDialog(false)}>
                <DialogTitle>Add New Category</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label="Category Name"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        fullWidth
                        sx={{ mt: 1 }}
                        placeholder="e.g., Balance of System"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddCategoryDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddCategory} variant="contained">Add</Button>
                </DialogActions>
            </Dialog>

            {/* Add Item Dialog */}
            <Dialog open={addItemDialog.open} onClose={() => setAddItemDialog({ open: false, categoryId: null })}>
                <DialogTitle>Add New Item</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label="Item Name"
                        value={newItem.name}
                        onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                        fullWidth
                        sx={{ mt: 1, mb: 2 }}
                        placeholder="e.g., Monitoring System"
                    />
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            label="Rate"
                            type="number"
                            value={newItem.rate || ''}
                            onChange={(e) => setNewItem(prev => ({ ...prev, rate: e.target.value }))}
                            sx={{ flex: 1 }}
                        />
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Unit</InputLabel>
                            <Select
                                value={newItem.unit}
                                onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                                label="Unit"
                            >
                                {UNIT_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <TextField
                        label="Description (optional)"
                        value={newItem.description}
                        onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                        fullWidth
                        multiline
                        rows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddItemDialog({ open: false, categoryId: null })}>Cancel</Button>
                    <Button onClick={handleAddItem} variant="contained">Add Item</Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={3000}
                onClose={() => setSuccessMessage('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </Card>
    );
}

export default CapExEditor;
